import React, { useEffect, useMemo, useState } from "react";
import {
  Form,
  Button,
  Container,
  Row,
  Col,
  InputGroup,
  Alert,
  Card,
  Spinner,
  Badge,
} from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faEye,
  faEyeSlash,
  faRotateRight,
  faFloppyDisk,
  faVialCircleCheck,
  faPenToSquare,
} from "@fortawesome/free-solid-svg-icons";
import axios from "./axiosConfig";

type MediaType = "SMTP" | "SMS" | "Webhook";
type AlertVariant = "success" | "danger" | "warning" | "info";

interface SMTPConfig {
  smtpHost: string;
  smtpPort: string;
  senderEmail: string;
  senderPassword: string;
}

interface SMSConfig {
  provider: string;
  apiUrl: string;
  apiKey: string;
  senderId: string;
}

interface WebhookConfig {
  url: string;
  method: string;
  authToken: string;
  customHeaders: string;
}

interface FormDataState {
  SMTP: SMTPConfig;
  SMS: SMSConfig;
  Webhook: WebhookConfig;
}

interface ExistingConfigItem {
  mediaType: MediaType;
  exists: boolean;
  summary: string[];
  rawData: any;
}

const defaultFormData: FormDataState = {
  SMTP: {
    smtpHost: "",
    smtpPort: "",
    senderEmail: "",
    senderPassword: "",
  },
  SMS: {
    provider: "",
    apiUrl: "",
    apiKey: "",
    senderId: "",
  },
  Webhook: {
    url: "",
    method: "POST",
    authToken: "",
    customHeaders: "",
  },
};

const mediaTypes: MediaType[] = ["SMTP", "SMS", "Webhook"];

const MediaConfigForm: React.FC = () => {
  const [mediaType, setMediaType] = useState<MediaType>("SMTP");
  const [formData, setFormData] = useState<FormDataState>(defaultFormData);

  const [showSecrets, setShowSecrets] = useState({
    smtpPassword: false,
    smsApiKey: false,
    webhookToken: false,
  });

  const [alertVisible, setAlertVisible] = useState(false);
  const [alertMessage, setAlertMessage] = useState("");
  const [alertVariant, setAlertVariant] = useState<AlertVariant>("success");

  const [loadingConfig, setLoadingConfig] = useState(false);
  const [savingConfig, setSavingConfig] = useState(false);
  const [testingConfig, setTestingConfig] = useState(false);
  const [loadingExistingConfigs, setLoadingExistingConfigs] = useState(false);

  const [existingConfigs, setExistingConfigs] = useState<ExistingConfigItem[]>([]);

  const mediaTypeLabel = useMemo(() => {
    switch (mediaType) {
      case "SMTP":
        return "Email (SMTP)";
      case "SMS":
        return "SMS";
      case "Webhook":
        return "Webhook";
      default:
        return mediaType;
    }
  }, [mediaType]);

  const showAlert = (message: string, variant: AlertVariant) => {
    setAlertMessage(message);
    setAlertVariant(variant);
    setAlertVisible(true);
  };

  const toggleSecretVisibility = (field: "smtpPassword" | "smsApiKey" | "webhookToken") => {
    setShowSecrets((prev) => ({
      ...prev,
      [field]: !prev[field],
    }));
  };

  const getConfigEndpoint = (type: MediaType) => {
    return `/api/media/${type.toLowerCase()}-config`;
  };

  const getTestEndpoint = (type: MediaType) => {
    return `/api/media/${type.toLowerCase()}-config/test`;
  };

  const handleFieldChange = (type: MediaType, field: string, value: string) => {
    setFormData((prev) => ({
      ...prev,
      [type]: {
        ...prev[type],
        [field]: value,
      },
    }));
  };

  const buildPayload = (type: MediaType) => {
    switch (type) {
      case "SMTP":
        return {
          smtpHost: formData.SMTP.smtpHost.trim(),
          smtpPort: formData.SMTP.smtpPort.trim(),
          senderEmail: formData.SMTP.senderEmail.trim(),
          senderPassword: formData.SMTP.senderPassword,
        };
      case "SMS":
        return {
          provider: formData.SMS.provider.trim(),
          apiUrl: formData.SMS.apiUrl.trim(),
          apiKey: formData.SMS.apiKey,
          senderId: formData.SMS.senderId.trim(),
        };
      case "Webhook":
        return {
          url: formData.Webhook.url.trim(),
          method: formData.Webhook.method.trim(),
          authToken: formData.Webhook.authToken,
          customHeaders: formData.Webhook.customHeaders.trim(),
        };
      default:
        return {};
    }
  };

  const applyLoadedConfig = (type: MediaType, data: any) => {
    if (!data || typeof data !== "object") {
      return;
    }

    if (type === "SMTP") {
      setFormData((prev) => ({
        ...prev,
        SMTP: {
          smtpHost: data.smtpHost ?? data.SMTPHost ?? "",
          smtpPort:
            data.smtpPort != null
              ? String(data.smtpPort)
              : data.SMTPPort != null
                ? String(data.SMTPPort)
                : "",
          senderEmail: data.senderEmail ?? data.SenderEmail ?? "",
          senderPassword: data.senderPassword ?? data.SenderPassword ?? "",
        },
      }));
      return;
    }

    if (type === "SMS") {
      setFormData((prev) => ({
        ...prev,
        SMS: {
          provider: data.provider ?? data.Provider ?? "",
          apiUrl: data.apiUrl ?? data.url ?? data.ApiUrl ?? "",
          apiKey: data.apiKey ?? data.ApiKey ?? "",
          senderId: data.senderId ?? data.SenderId ?? "",
        },
      }));
      return;
    }

    if (type === "Webhook") {
      setFormData((prev) => ({
        ...prev,
        Webhook: {
          url: data.url ?? data.URL ?? "",
          method: data.method ?? data.Method ?? "POST",
          authToken: data.authToken ?? data.token ?? data.AuthToken ?? "",
          customHeaders:
            typeof data.customHeaders === "string"
              ? data.customHeaders
              : typeof data.CustomHeaders === "string"
                ? data.CustomHeaders
                : data.customHeaders || data.CustomHeaders
                  ? JSON.stringify(data.customHeaders ?? data.CustomHeaders, null, 2)
                  : "",
        },
      }));
    }
  };

  const buildConfigSummary = (type: MediaType, data: any): string[] => {
    if (!data || typeof data !== "object") {
      return [];
    }

    switch (type) {
      case "SMTP":
        return [
          data.smtpHost || data.SMTPHost ? `Host: ${data.smtpHost || data.SMTPHost}` : "Host: -",
          data.smtpPort || data.SMTPPort ? `Port: ${data.smtpPort || data.SMTPPort}` : "Port: -",
          data.senderEmail || data.SenderEmail
            ? `Sender: ${data.senderEmail || data.SenderEmail}`
            : "Sender: -",
          data.senderPassword || data.SenderPassword
            ? "Password: Configured"
            : "Password: Not configured",
        ];
      case "SMS":
        return [
          data.provider || data.Provider ? `Provider: ${data.provider || data.Provider}` : "Provider: -",
          data.apiUrl || data.url || data.ApiUrl
            ? `API URL: ${data.apiUrl || data.url || data.ApiUrl}`
            : "API URL: -",
          data.senderId || data.SenderId ? `Sender ID: ${data.senderId || data.SenderId}` : "Sender ID: -",
          data.apiKey || data.ApiKey ? "API Key: Configured" : "API Key: Not configured",
        ];
      case "Webhook":
        return [
          data.url || data.URL ? `URL: ${data.url || data.URL}` : "URL: -",
          data.method || data.Method ? `Method: ${data.method || data.Method}` : "Method: -",
          data.authToken || data.token || data.AuthToken
            ? "Auth Token: Configured"
            : "Auth Token: Not configured",
          data.customHeaders || data.CustomHeaders
            ? "Headers: Configured"
            : "Headers: Not configured",
        ];
      default:
        return [];
    }
  };

  const fetchConfigData = async (type: MediaType) => {
    const response = await axios.get(getConfigEndpoint(type));
    return response?.data?.data ?? response?.data;
  };

  const loadConfig = async (type: MediaType, silent = false) => {
    setLoadingConfig(true);

    try {
      const responseData = await fetchConfigData(type);
      applyLoadedConfig(type, responseData);

      if (!silent) {
        showAlert(`${type} configuration loaded successfully.`, "success");
      }
    } catch (error: any) {
      const status = error?.response?.status;
      const backendError = error?.response?.data?.error;

      if (status === 404) {
        applyLoadedConfig(type, {});
        if (!silent) {
          showAlert(`No existing ${type} configuration found.`, "info");
        }
      } else {
        console.error(`Failed to load ${type} configuration:`, backendError || error);
        if (!silent) {
          showAlert(backendError || `Failed to load ${type} configuration.`, "danger");
        }
      }
    } finally {
      setLoadingConfig(false);
    }
  };

  const loadAllExistingConfigs = async (silent = true) => {
    setLoadingExistingConfigs(true);

    try {
      const results = await Promise.all(
        mediaTypes.map(async (type) => {
          try {
            const responseData = await fetchConfigData(type);

            return {
              mediaType: type,
              exists: !!responseData && Object.keys(responseData).length > 0,
              summary: buildConfigSummary(type, responseData),
              rawData: responseData,
            } as ExistingConfigItem;
          } catch (error: any) {
            if (error?.response?.status === 404) {
              return {
                mediaType: type,
                exists: false,
                summary: [],
                rawData: null,
              } as ExistingConfigItem;
            }

            return {
              mediaType: type,
              exists: false,
              summary: ["Failed to load configuration"],
              rawData: null,
            } as ExistingConfigItem;
          }
        })
      );

      setExistingConfigs(results);

      if (!silent) {
        showAlert("Existing configuration list refreshed successfully.", "success");
      }
    } catch (error) {
      console.error("Failed to refresh existing configuration list:", error);
      if (!silent) {
        showAlert("Failed to refresh existing configuration list.", "danger");
      }
    } finally {
      setLoadingExistingConfigs(false);
    }
  };

  const handleSelectExistingConfig = (item: ExistingConfigItem) => {
    if (!item.exists || !item.rawData) {
      return;
    }

    setMediaType(item.mediaType);
    applyLoadedConfig(item.mediaType, item.rawData);
    showAlert(`${item.mediaType} configuration loaded into the form.`, "info");
  };

  useEffect(() => {
    loadConfig("SMTP", true);
    loadAllExistingConfigs(true);
  }, []);

  useEffect(() => {
    loadConfig(mediaType, true);
  }, [mediaType]);

  const validateWebhookHeaders = () => {
    if (mediaType !== "Webhook") {
      return true;
    }

    const headers = formData.Webhook.customHeaders.trim();
    if (!headers) {
      return true;
    }

    try {
      const parsed = JSON.parse(headers);
      if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) {
        showAlert("Webhook Headers must be a valid JSON object.", "warning");
        return false;
      }
      return true;
    } catch (error) {
      showAlert("Webhook Headers must be a valid JSON object.", "warning");
      return false;
    }
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateWebhookHeaders()) {
      return;
    }

    setSavingConfig(true);

    try {
      const payload = buildPayload(mediaType);
      await axios.post(getConfigEndpoint(mediaType), payload);

      showAlert(`${mediaType} configuration saved successfully.`, "success");
      await loadAllExistingConfigs(true);
    } catch (error: any) {
      const backendError =
        error?.response?.data?.error ||
        `Failed to save ${mediaType} configuration. Please try again.`;

      console.error(`Failed to save ${mediaType} configuration:`, backendError);
      showAlert(backendError, "danger");
    } finally {
      setSavingConfig(false);
    }
  };

  const handleTest = async () => {
    if (!validateWebhookHeaders()) {
      return;
    }

    setTestingConfig(true);

    try {
      const payload = buildPayload(mediaType);
      const response = await axios.post(getTestEndpoint(mediaType), payload);

      const successMessage =
        response?.data?.message ||
        response?.data?.data?.message ||
        `${mediaType} test completed successfully.`;

      showAlert(successMessage, "success");
    } catch (error: any) {
      const backendError =
        error?.response?.data?.error ||
        error?.response?.data?.message ||
        `Failed to test ${mediaType} configuration.`;

      console.error(`Failed to test ${mediaType} configuration:`, backendError);
      showAlert(backendError, "danger");
    } finally {
      setTestingConfig(false);
    }
  };

  const renderSMTPForm = () => {
    return (
      <>
        <Form.Group as={Row} className="mb-3" controlId="formSmtpHost">
          <Form.Label column sm="2">
            SMTP Host
          </Form.Label>
          <Col sm="10">
            <Form.Control
              type="text"
              value={formData.SMTP.smtpHost}
              onChange={(e) => handleFieldChange("SMTP", "smtpHost", e.target.value)}
              placeholder="Enter SMTP Host"
              required
              onInvalid={(e) => {
                const target = e.target as HTMLInputElement;
                target.setCustomValidity("Please enter the SMTP host.");
              }}
              onInput={(e) => {
                const target = e.target as HTMLInputElement;
                target.setCustomValidity("");
              }}
            />
          </Col>
        </Form.Group>

        <Form.Group as={Row} className="mb-3" controlId="formSmtpPort">
          <Form.Label column sm="2">
            SMTP Port
          </Form.Label>
          <Col sm="10">
            <Form.Control
              type="number"
              value={formData.SMTP.smtpPort}
              onChange={(e) => handleFieldChange("SMTP", "smtpPort", e.target.value)}
              placeholder="Enter SMTP Port"
              required
              onInvalid={(e) => {
                const target = e.target as HTMLInputElement;
                target.setCustomValidity("Please enter the SMTP port.");
              }}
              onInput={(e) => {
                const target = e.target as HTMLInputElement;
                target.setCustomValidity("");
              }}
            />
          </Col>
        </Form.Group>

        <Form.Group as={Row} className="mb-3" controlId="formSenderEmail">
          <Form.Label column sm="2">
            Sender Email
          </Form.Label>
          <Col sm="10">
            <Form.Control
              type="email"
              value={formData.SMTP.senderEmail}
              onChange={(e) => handleFieldChange("SMTP", "senderEmail", e.target.value)}
              placeholder="Enter Sender Email"
              required
              onInvalid={(e) => {
                const target = e.target as HTMLInputElement;
                target.setCustomValidity("Please enter a valid email address.");
              }}
              onInput={(e) => {
                const target = e.target as HTMLInputElement;
                target.setCustomValidity("");
              }}
            />
          </Col>
        </Form.Group>

        <Form.Group as={Row} className="mb-3" controlId="formSenderPassword">
          <Form.Label column sm="2">
            Sender Password
          </Form.Label>
          <Col sm="10">
            <InputGroup>
              <Form.Control
                type={showSecrets.smtpPassword ? "text" : "password"}
                value={formData.SMTP.senderPassword}
                onChange={(e) => handleFieldChange("SMTP", "senderPassword", e.target.value)}
                placeholder="Enter Sender Password"
                required
                onInvalid={(e) => {
                  const target = e.target as HTMLInputElement;
                  target.setCustomValidity("Please enter the sender password.");
                }}
                onInput={(e) => {
                  const target = e.target as HTMLInputElement;
                  target.setCustomValidity("");
                }}
              />
              <InputGroup.Text
                onClick={() => toggleSecretVisibility("smtpPassword")}
                style={{ cursor: "pointer" }}
              >
                <FontAwesomeIcon icon={showSecrets.smtpPassword ? faEyeSlash : faEye} />
              </InputGroup.Text>
            </InputGroup>
          </Col>
        </Form.Group>
      </>
    );
  };

  const renderSMSForm = () => {
    return (
      <>
        <Form.Group as={Row} className="mb-3" controlId="formSmsProvider">
          <Form.Label column sm="2">
            Provider
          </Form.Label>
          <Col sm="10">
            <Form.Control
              type="text"
              value={formData.SMS.provider}
              onChange={(e) => handleFieldChange("SMS", "provider", e.target.value)}
              placeholder="Enter SMS provider"
              required
              onInvalid={(e) => {
                const target = e.target as HTMLInputElement;
                target.setCustomValidity("Please enter the SMS provider.");
              }}
              onInput={(e) => {
                const target = e.target as HTMLInputElement;
                target.setCustomValidity("");
              }}
            />
          </Col>
        </Form.Group>

        <Form.Group as={Row} className="mb-3" controlId="formSmsApiUrl">
          <Form.Label column sm="2">
            API URL
          </Form.Label>
          <Col sm="10">
            <Form.Control
              type="url"
              value={formData.SMS.apiUrl}
              onChange={(e) => handleFieldChange("SMS", "apiUrl", e.target.value)}
              placeholder="Enter SMS API URL"
              required
              onInvalid={(e) => {
                const target = e.target as HTMLInputElement;
                target.setCustomValidity("Please enter a valid SMS API URL.");
              }}
              onInput={(e) => {
                const target = e.target as HTMLInputElement;
                target.setCustomValidity("");
              }}
            />
          </Col>
        </Form.Group>

        <Form.Group as={Row} className="mb-3" controlId="formSmsApiKey">
          <Form.Label column sm="2">
            API Key
          </Form.Label>
          <Col sm="10">
            <InputGroup>
              <Form.Control
                type={showSecrets.smsApiKey ? "text" : "password"}
                value={formData.SMS.apiKey}
                onChange={(e) => handleFieldChange("SMS", "apiKey", e.target.value)}
                placeholder="Enter SMS API Key"
                required
                onInvalid={(e) => {
                  const target = e.target as HTMLInputElement;
                  target.setCustomValidity("Please enter the SMS API key.");
                }}
                onInput={(e) => {
                  const target = e.target as HTMLInputElement;
                  target.setCustomValidity("");
                }}
              />
              <InputGroup.Text
                onClick={() => toggleSecretVisibility("smsApiKey")}
                style={{ cursor: "pointer" }}
              >
                <FontAwesomeIcon icon={showSecrets.smsApiKey ? faEyeSlash : faEye} />
              </InputGroup.Text>
            </InputGroup>
          </Col>
        </Form.Group>

        <Form.Group as={Row} className="mb-3" controlId="formSmsSenderId">
          <Form.Label column sm="2">
            Sender ID
          </Form.Label>
          <Col sm="10">
            <Form.Control
              type="text"
              value={formData.SMS.senderId}
              onChange={(e) => handleFieldChange("SMS", "senderId", e.target.value)}
              placeholder="Enter SMS Sender ID"
            />
          </Col>
        </Form.Group>
      </>
    );
  };

  const renderWebhookForm = () => {
    return (
      <>
        <Form.Group as={Row} className="mb-3" controlId="formWebhookUrl">
          <Form.Label column sm="2">
            URL
          </Form.Label>
          <Col sm="10">
            <Form.Control
              type="url"
              value={formData.Webhook.url}
              onChange={(e) => handleFieldChange("Webhook", "url", e.target.value)}
              placeholder="Enter Webhook URL"
              required
              onInvalid={(e) => {
                const target = e.target as HTMLInputElement;
                target.setCustomValidity("Please enter a valid Webhook URL.");
              }}
              onInput={(e) => {
                const target = e.target as HTMLInputElement;
                target.setCustomValidity("");
              }}
            />
          </Col>
        </Form.Group>

        <Form.Group as={Row} className="mb-3" controlId="formWebhookMethod">
          <Form.Label column sm="2">
            Method
          </Form.Label>
          <Col sm="10">
            <Form.Select
              value={formData.Webhook.method}
              onChange={(e) => handleFieldChange("Webhook", "method", e.target.value)}
            >
              <option value="POST">POST</option>
              <option value="GET">GET</option>
              <option value="PUT">PUT</option>
            </Form.Select>
          </Col>
        </Form.Group>

        <Form.Group as={Row} className="mb-3" controlId="formWebhookAuthToken">
          <Form.Label column sm="2">
            Auth Token
          </Form.Label>
          <Col sm="10">
            <InputGroup>
              <Form.Control
                type={showSecrets.webhookToken ? "text" : "password"}
                value={formData.Webhook.authToken}
                onChange={(e) => handleFieldChange("Webhook", "authToken", e.target.value)}
                placeholder="Enter Webhook Auth Token"
              />
              <InputGroup.Text
                onClick={() => toggleSecretVisibility("webhookToken")}
                style={{ cursor: "pointer" }}
              >
                <FontAwesomeIcon icon={showSecrets.webhookToken ? faEyeSlash : faEye} />
              </InputGroup.Text>
            </InputGroup>
          </Col>
        </Form.Group>

        <Form.Group as={Row} className="mb-3" controlId="formWebhookHeaders">
          <Form.Label column sm="2">
            Headers
          </Form.Label>
          <Col sm="10">
            <Form.Control
              as="textarea"
              rows={5}
              value={formData.Webhook.customHeaders}
              onChange={(e) => handleFieldChange("Webhook", "customHeaders", e.target.value)}
              placeholder={'Example:\n{\n  "Content-Type": "application/json",\n  "X-Token": "abc"\n}'}
            />
            <Form.Text className="text-muted">
              Please enter a valid JSON object if custom headers are needed.
            </Form.Text>
          </Col>
        </Form.Group>
      </>
    );
  };

  const renderFormByType = () => {
    switch (mediaType) {
      case "SMTP":
        return renderSMTPForm();
      case "SMS":
        return renderSMSForm();
      case "Webhook":
        return renderWebhookForm();
      default:
        return null;
    }
  };

  return (
    <Container className="mt-4 mb-5">
      <Card className="shadow-sm">
        <Card.Body>
          <div className="d-flex justify-content-between align-items-center mb-4">
            <h2 className="mb-0">Media Configuration</h2>
            <Button
              variant="outline-secondary"
              onClick={() => loadConfig(mediaType, false)}
              disabled={loadingConfig || savingConfig || testingConfig}
            >
              {loadingConfig ? (
                <>
                  <Spinner animation="border" size="sm" className="me-2" />
                  Loading...
                </>
              ) : (
                <>
                  <FontAwesomeIcon icon={faRotateRight} className="me-2" />
                  Reload
                </>
              )}
            </Button>
          </div>

          {alertVisible && (
            <Alert
              variant={alertVariant}
              dismissible
              onClose={() => setAlertVisible(false)}
            >
              {alertMessage}
            </Alert>
          )}

          <Form onSubmit={handleSave}>
            <Form.Group as={Row} className="mb-4" controlId="formMediaType">
              <Form.Label column sm="2">
                Media Type
              </Form.Label>
              <Col sm="10">
                <Form.Select
                  value={mediaType}
                  onChange={(e) => setMediaType(e.target.value as MediaType)}
                  disabled={loadingConfig || savingConfig || testingConfig}
                >
                  <option value="SMTP">Email (SMTP)</option>
                  <option value="SMS">SMS</option>
                  <option value="Webhook">Webhook</option>
                </Form.Select>
              </Col>
            </Form.Group>

            {loadingConfig ? (
              <div className="text-center py-4">
                <Spinner animation="border" />
                <div className="mt-2">Loading {mediaTypeLabel} configuration...</div>
              </div>
            ) : (
              renderFormByType()
            )}

            <div className="d-flex justify-content-end gap-2 mt-4 flex-wrap">
              <Button
                variant="outline-primary"
                type="button"
                onClick={handleTest}
                disabled={loadingConfig || savingConfig || testingConfig}
              >
                {testingConfig ? (
                  <>
                    <Spinner animation="border" size="sm" className="me-2" />
                    Testing...
                  </>
                ) : (
                  <>
                    <FontAwesomeIcon icon={faVialCircleCheck} className="me-2" />
                    Test Configuration
                  </>
                )}
              </Button>

              <Button
                variant="primary"
                type="submit"
                disabled={loadingConfig || savingConfig || testingConfig}
              >
                {savingConfig ? (
                  <>
                    <Spinner animation="border" size="sm" className="me-2" />
                    Saving...
                  </>
                ) : (
                  <>
                    <FontAwesomeIcon icon={faFloppyDisk} className="me-2" />
                    Save Configuration
                  </>
                )}
              </Button>
            </div>
          </Form>
        </Card.Body>
      </Card>

      <Card className="shadow-sm mt-4">
        <Card.Body>
          <div className="d-flex justify-content-between align-items-center mb-3">
            <h4 className="mb-0">Existing Configurations</h4>
            <Button
              variant="outline-secondary"
              size="sm"
              onClick={() => loadAllExistingConfigs(false)}
              disabled={loadingExistingConfigs}
            >
              {loadingExistingConfigs ? (
                <>
                  <Spinner animation="border" size="sm" className="me-2" />
                  Refreshing...
                </>
              ) : (
                <>
                  <FontAwesomeIcon icon={faRotateRight} className="me-2" />
                  Refresh List
                </>
              )}
            </Button>
          </div>

          {loadingExistingConfigs ? (
            <div className="text-center py-3">
              <Spinner animation="border" size="sm" />
              <div className="mt-2">Loading existing configurations...</div>
            </div>
          ) : (
            <Row>
              {existingConfigs.map((item) => (
                <Col md={4} className="mb-3" key={item.mediaType}>
                  <Card className={`h-100 shadow-sm ${item.exists ? "border-success" : "border-light"}`}>
                    <Card.Body className="d-flex flex-column">
                      <div className="d-flex justify-content-between align-items-center mb-3">
                        <h5 className="mb-0">{item.mediaType}</h5>
                        <Badge bg={item.exists ? "success" : "secondary"}>
                          {item.exists ? "Configured" : "Not Configured"}
                        </Badge>
                      </div>

                      {item.exists ? (
                        <>
                          <ul className="ps-3 mb-3" style={{ minHeight: "110px" }}>
                            {item.summary.map((line, index) => (
                              <li key={index}>{line}</li>
                            ))}
                          </ul>

                          <div className="mt-auto">
                            <Button
                              variant="outline-primary"
                              size="sm"
                              onClick={() => handleSelectExistingConfig(item)}
                            >
                              <FontAwesomeIcon icon={faPenToSquare} className="me-2" />
                              Edit This Configuration
                            </Button>
                          </div>
                        </>
                      ) : (
                        <div className="text-muted mt-2">No configuration saved yet.</div>
                      )}
                    </Card.Body>
                  </Card>
                </Col>
              ))}
            </Row>
          )}
        </Card.Body>
      </Card>
    </Container>
  );
};

export default MediaConfigForm;